// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { type PluginListenerHandle } from "@capacitor/core";
import { type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { addBrowserListener, openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useGetRequirements } from "@src/appV2/redesign/Documents/api/useGetRequirements";
import { useHelloSignEmbeddedFlow } from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/useHelloSignEmbeddedFlow";
import { useHelloSignFallbackUrl } from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/useHelloSignFallbackUrl";
import { head } from "lodash";
import { type RefObject, useEffect, useRef } from "react";

import { type PlacementDetail } from "../../api/useGetPlacement";

interface UseSupportChatEmbeddedHelloSignParams {
  placementDetails: PlacementDetail;
  onSignSuccess?: () => void;
}

interface UseSupportChatEmbeddedHelloSignResult {
  isHelloSignDocument: boolean;
  helloSignButtonText?: string;
  isLoadingHelloSign: boolean;
  handleSignDocument?: () => Promise<void>;
  helloSignIframeContainerRef?: RefObject<HTMLDivElement>;
  helloSignFormModalState?: UseModalState;
  submittingDocumentModalState?: UseModalState;
}

function getFirstMissingDocumentRequirementId(
  placementDetails: PlacementDetail
): string | undefined {
  const missingDocuments = placementDetails.workerRequirements?.filter(
    (requirement) => !requirement.attributes.isValid
  );
  const firstMissingDocument = head(missingDocuments);

  return firstMissingDocument?.relationships.requirement.data.id;
}

// Given a worker's placement details, this hook will determine if the worker has a composite document or a direct HelloSign document missing and return the appropriate information to display a button to sign the document.
export function useSupportChatEmbeddedHelloSign(
  params: UseSupportChatEmbeddedHelloSignParams
): UseSupportChatEmbeddedHelloSignResult {
  const { placementDetails, onSignSuccess } = params;
  const { showErrorToast, showSuccessToast, showInfoToast } = useToast();
  const helloSignIframeContainerRef = useRef<HTMLDivElement>(null);
  const browserFinishedListenerRef = useRef<PluginListenerHandle | null>(null);
  const helloSignFormModalState = useModalState();
  const submittingDocumentModalState = useModalState();
  const { openModal: openSubmittingDocumentModal } = submittingDocumentModalState;
  const shouldUseHelloSignFallbackUrl = useHelloSignFallbackUrl();

  const firstMissingDocumentRequirementId = getFirstMissingDocumentRequirementId(placementDetails);
  const hasMissingRequirement = isDefined(firstMissingDocumentRequirementId);

  const { data: requirementDetails, isLoading: isLoadingRequirement } = useGetRequirements(
    {
      filter: {
        id: firstMissingDocumentRequirementId,
      },
    },
    {
      enabled: hasMissingRequirement,
    }
  );

  const requirementAttributes = head(requirementDetails?.data)?.attributes;
  const compositeConfig = requirementAttributes?.compositeConfig;
  const compositeRequirements = compositeConfig?.requirements;
  const compositeHelloSignRequirement = head(compositeRequirements);
  const isCompositeDocument =
    isDefined(compositeConfig) && isDefined(compositeHelloSignRequirement);

  const directHelloSignConfig = requirementAttributes?.helloSignConfig;
  const isDirectHelloSignDocument = isDefined(directHelloSignConfig) && !isCompositeDocument;

  // For composite documents, fetch the child HelloSign requirement details
  const { data: helloSignRequirementDetails, isLoading: isLoadingHelloSignRequirement } =
    useGetRequirements(
      {
        filter: {
          id: compositeHelloSignRequirement?.requirementId,
        },
      },
      {
        enabled: isCompositeDocument && isDefined(compositeHelloSignRequirement?.requirementId),
      }
    );

  const helloSignConfig = isCompositeDocument
    ? head(helloSignRequirementDetails?.data)?.attributes.helloSignConfig
    : directHelloSignConfig;
  const fallbackStaticUrl = helloSignConfig?.fallbackStaticUrl;
  const isEmbeddedFlowEnabled = helloSignConfig?.isEmbeddedFlowEnabled;

  const { isCreatingHelloSignSession, isSubmittingHelloSignDocument, startHelloSignEmbeddedFlow } =
    useHelloSignEmbeddedFlow({
      requirementId: firstMissingDocumentRequirementId ?? "",
      compositeChildRequirementId: compositeHelloSignRequirement?.requirementId,
      onSuccess: () => {
        submittingDocumentModalState.closeModal();
        helloSignFormModalState.closeModal();
        showSuccessToast("Document signed successfully");
        onSignSuccess?.();
      },
      onError: () => {
        helloSignFormModalState.closeModal();
        submittingDocumentModalState.closeModal();
        showErrorToast("Failed to sign document. Please try again.");
      },
      helloSignIframeContainerRef,
      onHelloSignClientCancel: () => {
        helloSignFormModalState.closeModal();
      },
      enabled: hasMissingRequirement,
    });

  useEffect(() => {
    if (isSubmittingHelloSignDocument) {
      openSubmittingDocumentModal();
    }
  }, [isSubmittingHelloSignDocument, openSubmittingDocumentModal]);

  useEffect(() => {
    return () => {
      void browserFinishedListenerRef.current?.remove();
    };
  }, []);

  async function handleSignDocument() {
    if (isDefined(fallbackStaticUrl) && (shouldUseHelloSignFallbackUrl || !isEmbeddedFlowEnabled)) {
      await browserFinishedListenerRef.current?.remove();

      browserFinishedListenerRef.current = await addBrowserListener("browserFinished", async () => {
        showInfoToast(
          "If you have signed this document, please check back here later for an update"
        );
        await browserFinishedListenerRef.current?.remove();
        browserFinishedListenerRef.current = null;
      });
      await openInBrowser({ url: fallbackStaticUrl });
    } else {
      helloSignFormModalState.openModal();
      void startHelloSignEmbeddedFlow();
    }
  }

  if (!hasMissingRequirement) {
    return {
      isHelloSignDocument: false,
      isLoadingHelloSign: false,
    };
  }

  const isHelloSignDocument = isCompositeDocument || isDirectHelloSignDocument;
  const isLoadingHelloSign =
    isLoadingRequirement ||
    isCreatingHelloSignSession ||
    (isCompositeDocument && isLoadingHelloSignRequirement);

  return {
    isHelloSignDocument,
    helloSignButtonText: isCompositeDocument ? compositeHelloSignRequirement?.ctaText : undefined,
    isLoadingHelloSign,
    handleSignDocument,
    helloSignIframeContainerRef,
    helloSignFormModalState,
    submittingDocumentModalState,
  };
}
